import {
  useGetDownloadSignedUrlForQuestionGroupsLazyQuery,
  useGetDownloadSignedUrlForQuestionsLazyQuery,
  type QuestionKeywordSearchItemDto,
} from '@exam-management/graphql';
import { extractImagesFileNames, updateImageTags } from '@exam-management/rich-text-editor';
import { useEffect, useState } from 'react';

type AssetMap = Record<string, string>;

const EMPTY_ITEMS: QuestionKeywordSearchItemDto[] = [];

function getQuestionFileNames(item: QuestionKeywordSearchItemDto): string[] {
  const contents = [
    item.details,
    item.solution?.value ?? '',
    item.metadata?.reference ?? '',
    ...item.metadata.choices.map((choice) => choice.label),
  ];
  return [...new Set(contents.flatMap(extractImagesFileNames))];
}

function applyAssets(
  item: QuestionKeywordSearchItemDto,
  questionAssets: AssetMap,
  groupAssets: AssetMap,
): QuestionKeywordSearchItemDto {
  return {
    ...item,
    details: updateImageTags(item.details, questionAssets),
    solution: item.solution
      ? { ...item.solution, value: updateImageTags(item.solution.value ?? '', questionAssets) }
      : item.solution,
    metadata: {
      ...item.metadata,
      reference: updateImageTags(item.metadata.reference ?? '', questionAssets),
      choices: item.metadata.choices.map((choice) => ({
        ...choice,
        label: updateImageTags(choice.label, questionAssets),
      })),
    },
    questionGroup: item.questionGroup
      ? {
          ...item.questionGroup,
          paragraph: updateImageTags(item.questionGroup.paragraph ?? '', groupAssets),
        }
      : item.questionGroup,
  };
}

/**
 * Resolves every image in a page of search results with two signed-url requests
 * (one for question-scoped content, one for item-set paragraphs) instead of
 * letting each card resolve its own.
 */
export function useResolvedSearchItems(items: QuestionKeywordSearchItemDto[] | undefined): {
  items: QuestionKeywordSearchItemDto[];
  loading: boolean;
} {
  const [resolved, setResolved] = useState<QuestionKeywordSearchItemDto[]>(items ?? EMPTY_ITEMS);
  const [loading, setLoading] = useState(false);
  const [getQuestionUrls] = useGetDownloadSignedUrlForQuestionsLazyQuery();
  const [getQuestionGroupUrls] = useGetDownloadSignedUrlForQuestionGroupsLazyQuery();

  useEffect(() => {
    const pageItems = items ?? EMPTY_ITEMS;

    const questionFileInputs = pageItems
      .map((item) => ({ questionId: item.id, fileNames: getQuestionFileNames(item) }))
      .filter((input) => input.fileNames.length > 0);

    // sub-questions of one item set share a paragraph, so merge by group id —
    // the API keys its response by id and would otherwise drop all but the last entry
    const groupFileNames = new Map<string, Set<string>>();
    pageItems.forEach((item) => {
      const group = item.questionGroup;
      if (!group?.paragraph) return;
      const fileNames = extractImagesFileNames(group.paragraph);
      if (fileNames.length === 0) return;
      const existing = groupFileNames.get(group.id) ?? new Set<string>();
      fileNames.forEach((fileName) => existing.add(fileName));
      groupFileNames.set(group.id, existing);
    });
    const questionGroupFileInputs = [...groupFileNames].map(([questionGroupId, fileNames]) => ({
      questionGroupId,
      fileNames: [...fileNames],
    }));

    if (questionFileInputs.length === 0 && questionGroupFileInputs.length === 0) {
      setResolved(pageItems);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    Promise.all([
      questionFileInputs.length > 0
        ? getQuestionUrls({ variables: { input: { questionFileInputs } } })
        : Promise.resolve(null),
      questionGroupFileInputs.length > 0
        ? getQuestionGroupUrls({ variables: { input: { questionGroupFileInputs } } })
        : Promise.resolve(null),
    ])
      .then(([questionResult, groupResult]) => {
        if (!active) return;
        const questionUrls: Record<string, AssetMap> =
          questionResult?.data?.qctGetDownloadSignedUrlForQuestions?.signedUrls ?? {};
        const groupUrls: Record<string, AssetMap> =
          groupResult?.data?.qctGetDownloadSignedUrlForQuestionGroups?.signedUrls ?? {};

        setResolved(
          pageItems.map((item) =>
            applyAssets(
              item,
              questionUrls[item.id] ?? {},
              (item.questionGroup && groupUrls[item.questionGroup.id]) || {},
            ),
          ),
        );
      })
      .catch(() => {
        // a stale search index makes the API reject the whole batch — fall back to
        // unresolved content rather than blanking every image on the page
        if (active) setResolved(pageItems);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [items, getQuestionUrls, getQuestionGroupUrls]);

  return { items: resolved, loading };
}
